import { fetchAdminSettings, patchAdminSettings } from '@/api/admin';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { formatApiError } from '@/lib/format-api-error';
import {
	PHOTO_MATCH_CATEGORIES,
	PHOTO_MATCH_CATEGORY_IDS,
	isPhotoMatchCategoryId,
	type PhotoMatchCategoryId,
} from '@crave/config/photo-match-categories';
import axios from 'axios';
import { Camera } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import SettingsWidgetCard from './settings-widget-card';

function formatPatchError(error: unknown, fallback: string): string {
	if (axios.isAxiosError(error)) {
		return formatApiError(error.response?.data) ?? fallback;
	}
	return fallback;
}

function normalizeAllowedCategories(values: string[] | undefined): PhotoMatchCategoryId[] {
	if (!values?.length) return [...PHOTO_MATCH_CATEGORY_IDS];
	const normalized = values.filter(isPhotoMatchCategoryId);
	return normalized.length > 0 ? normalized : [...PHOTO_MATCH_CATEGORY_IDS];
}

export default function PhotosAiSettingsWidget() {
	const [enabled, setEnabled] = useState(false);
	const [allowedCategories, setAllowedCategories] = useState<PhotoMatchCategoryId[]>([]);
	const [promptExtra, setPromptExtra] = useState('');
	const [model, setModel] = useState('glm-4.6v-flashx');
	const [endpoint, setEndpoint] = useState('https://api.z.ai/api/paas/v4/chat/completions');
	const [scanMaxDays, setScanMaxDays] = useState(200);
	const [scanMaxCount, setScanMaxCount] = useState(1000);
	const [tgChatId, setTgChatId] = useState('');
	const [apiKey, setApiKey] = useState('');
	const [apiKeyMasked, setApiKeyMasked] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		let cancelled = false;

		async function load() {
			try {
				const settings = await fetchAdminSettings();
				if (!cancelled) {
					setEnabled(settings.photosAiEnabled);
					setAllowedCategories(normalizeAllowedCategories(settings.photosAiAllowedCategories));
					setPromptExtra(settings.photosAiPromptExtra ?? '');
					setModel(settings.photosAiModel ?? 'glm-4.6v-flashx');
					setEndpoint(settings.photosAiEndpoint ?? 'https://api.z.ai/api/paas/v4/chat/completions');
					setScanMaxDays(settings.photosScanMaxDays ?? 200);
					setScanMaxCount(settings.photosScanMaxCount ?? 1000);
					setTgChatId(settings.photosTgChatId ?? '');
					setApiKey('');
					setApiKeyMasked(settings.photosAiApiKey ?? null);
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		void load();

		return () => {
			cancelled = true;
		};
	}, []);

	function toggleCategory(id: PhotoMatchCategoryId) {
		setAllowedCategories(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
	}

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		if (enabled && allowedCategories.length === 0) {
			toast.error('Выберите хотя бы одну категорию');
			return;
		}

		setIsSaving(true);
		try {
			const patch: Parameters<typeof patchAdminSettings>[0] = {
				photosAiEnabled: enabled,
				photosAiAllowedCategories: allowedCategories,
				photosAiPromptExtra: promptExtra,
				photosAiModel: model,
				photosAiEndpoint: endpoint,
				photosScanMaxDays: scanMaxDays,
				photosScanMaxCount: scanMaxCount,
				photosTgChatId: tgChatId,
			};
			if (apiKey.trim()) {
				patch.photosAiApiKey = apiKey.trim();
			}

			const settings = await patchAdminSettings(patch, { suppressToast: true });
			setEnabled(settings.photosAiEnabled);
			setAllowedCategories(normalizeAllowedCategories(settings.photosAiAllowedCategories));
			setApiKey('');
			setApiKeyMasked(settings.photosAiApiKey ?? null);
			toast.success('Настройки фото AI сохранены');
		} catch (error) {
			toast.error(formatPatchError(error, 'Не удалось сохранить настройки'));
		} finally {
			setIsSaving(false);
		}
	}

	return (
		<SettingsWidgetCard
			id='photos-ai'
			icon={Camera}
			title='Фото AI'
			description='Скан галереи на устройстве и загрузка совпавших фото'
		>
			<LoadingWrapper show={isSaving}>
				<CardContent className='px-4 py-4'>
					<form className='flex flex-col gap-4' onSubmit={handleSubmit}>
						<div className='flex items-center justify-between gap-4'>
							<div>
								<Label>Скан фото на устройстве</Label>
								<p className='text-xs text-muted-foreground'>
									Требует API-ключ Z.AI (в настройках или env) и photosEnabled в билде
								</p>
							</div>
							<Switch checked={enabled} onCheckedChange={setEnabled} disabled={isLoading || isSaving} />
						</div>

						<Separator />

						<div className='flex flex-col gap-2'>
							<Label htmlFor='photos-ai-api-key'>API-ключ Z.AI</Label>
							<p className='text-xs text-muted-foreground'>
								{apiKeyMasked
									? `Ключ задан (${apiKeyMasked}). Оставьте поле пустым, чтобы не менять.`
									: 'Ключ не задан в настройках. Используется ZAI_API_KEY из env, если задан.'}
							</p>
							<Input
								id='photos-ai-api-key'
								type='password'
								autoComplete='off'
								placeholder='••••••••'
								value={apiKey}
								onChange={e => setApiKey(e.target.value)}
								disabled={isLoading || isSaving}
							/>
							{apiKeyMasked && (
								<Button
									type='button'
									variant='outline'
									size='sm'
									className='self-start'
									disabled={isLoading || isSaving}
									onClick={() => {
										setApiKey('');
										void (async () => {
											setIsSaving(true);
											try {
												const settings = await patchAdminSettings(
													{ photosAiApiKey: null },
													{ suppressToast: true },
												);
												setApiKeyMasked(settings.photosAiApiKey ?? null);
												toast.success('Override ключа сброшен, используется env');
											} catch (error) {
												toast.error(formatPatchError(error, 'Не удалось сбросить ключ'));
											} finally {
												setIsSaving(false);
											}
										})();
									}}
								>
									Сбросить override (использовать env)
								</Button>
							)}
						</div>

						<div className='flex flex-col gap-2'>
							<Label>Категории для загрузки</Label>
							<div className='flex flex-wrap gap-2'>
								{PHOTO_MATCH_CATEGORY_IDS.map(id => {
									const active = allowedCategories.includes(id);
									return (
										<Button
											key={id}
											type='button'
											size='sm'
											variant={active ? 'default' : 'outline'}
											onClick={() => toggleCategory(id)}
											disabled={isLoading || isSaving}
										>
											{PHOTO_MATCH_CATEGORIES[id].label}
										</Button>
									);
								})}
							</div>
						</div>

						<div className='grid gap-4 md:grid-cols-2'>
							<div className='flex flex-col gap-2'>
								<Label htmlFor='photos-scan-max-days'>Макс. дней назад</Label>
								<Input
									id='photos-scan-max-days'
									type='number'
									min={1}
									max={3650}
									value={scanMaxDays}
									onChange={e => setScanMaxDays(Number(e.target.value))}
									disabled={isLoading || isSaving}
								/>
							</div>
							<div className='flex flex-col gap-2'>
								<Label htmlFor='photos-scan-max-count'>Макс. фото за скан</Label>
								<Input
									id='photos-scan-max-count'
									type='number'
									min={1}
									max={50000}
									value={scanMaxCount}
									onChange={e => setScanMaxCount(Number(e.target.value))}
									disabled={isLoading || isSaving}
								/>
							</div>
						</div>

						<div className='flex flex-col gap-2'>
							<Label htmlFor='photos-prompt-extra'>Доп. указания для промпта</Label>
							<Textarea
								id='photos-prompt-extra'
								value={promptExtra}
								onChange={e => setPromptExtra(e.target.value)}
								rows={3}
								disabled={isLoading || isSaving}
							/>
						</div>

						<div className='grid gap-4 md:grid-cols-2'>
							<div className='flex flex-col gap-2'>
								<Label htmlFor='photos-ai-model'>Модель</Label>
								<Input
									id='photos-ai-model'
									value={model}
									onChange={e => setModel(e.target.value)}
									disabled={isLoading || isSaving}
								/>
							</div>
							<div className='flex flex-col gap-2'>
								<Label htmlFor='photos-ai-endpoint'>Endpoint</Label>
								<Input
									id='photos-ai-endpoint'
									value={endpoint}
									onChange={e => setEndpoint(e.target.value)}
									disabled={isLoading || isSaving}
								/>
							</div>
						</div>

						<div className='flex flex-col gap-2'>
							<Label htmlFor='photos-tg-chat-id'>TG chat id (опционально)</Label>
							<Input
								id='photos-tg-chat-id'
								value={tgChatId}
								onChange={e => setTgChatId(e.target.value)}
								placeholder='Переопределить PHOTOS_CHAT_ID'
								disabled={isLoading || isSaving}
							/>
						</div>

						<Button type='submit' disabled={isLoading || isSaving}>Сохранить</Button>
					</form>
				</CardContent>
			</LoadingWrapper>
		</SettingsWidgetCard>
	);
}
